import { Participant, StatusCounts, SessionStatusCounts } from './types';
import { formatDisplayTimeWithSeconds } from '@/utils/timeDisplayFormat';
import {
  SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
  SCHEDULER_SESSION_STATUS_FILTER_OPTIONS,
  MAX_PARTICIPANT_OVERBOOKS,
  type SchedulerParticipantStatusFilterGroup,
  type SchedulerSessionStatusFilterOption,
} from './constants';
import { isSessionStatusEmpty } from '@/shared/lib/session-status-utils';
import { formatStatusDisplay as displayParticipantScheduleStatus } from '@/shared/lib/participant-schedule-status-display';
import { getSessionStatusCardStyle } from '@/shared/lib/status-visual-palette';
import {hasRemainingWindowCapCapacity} from './participantWindowCap';

/** Stringify a primitive value; non-primitives (and null/undefined) fall back to `fallback`. */
function safeString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
    ? String(value)
    : fallback;
}

export const SCHEDULE_STATUS = {
  ENDORSED: 'endorsed',
  CONTACTING: 'contacting',
  SCHEDULED: 'scheduled',
  UNREACHABLE: 'unreachable',
  AVAILABLE: 'available',
  DROPOUT: 'dropout',
  RESCHEDULE: 'reschedule',
  RESCHEDULED: 'rescheduled',
  CALLED_OFF: 'called_off',
} as const;

export const SESSION_FETCH_STATUSES = [
  SCHEDULE_STATUS.SCHEDULED,
  SCHEDULE_STATUS.RESCHEDULE,
  SCHEDULE_STATUS.RESCHEDULED,
] as const;

export const SCHEDULED_LIKE_STATUSES = [
  SCHEDULE_STATUS.SCHEDULED,
  SCHEDULE_STATUS.RESCHEDULED,
] as const;

export const TERMINAL_SCHEDULE_STATUSES = new Set([
  'cancelled', 'canceled', 'completed', 'no show', 'no-show', 'no_show', 'noshow',
  'called off', 'called_off', 'calledoff', 'disqualified', 'blocked', 'overbooked',
  'booking_limit_reached', 'max_overbooks_reached',
]);

/**
 * Session Status Definitions (schedule.status – the status of the booked session)
 * Scheduled – When the session is booked.
 * In Progress – When the session has started.
 * Completed – When the session is successfully finished.
 * Cancelled – When the session is cancelled by the scheduler.
 * Called Off – When the session is called off.
 * No Show – When the participant does not attend the session.
 * Reschedule – When a reschedule request is raised by the moderator.
 * Rescheduled – When the session has been successfully rescheduled after the moderator's request.
 */
export {
  SESSION_STATUS_DISPLAY,
  formatSessionStatusDisplay,
  formatSessionStatusCellDisplay,
  getSessionStatusBadge,
  TO_BE_SCHEDULED_DISPLAY,
  isSessionStatusEmpty,
} from '@/shared/lib/session-status-utils';

export const getSessionStatusColor = getSessionStatusCardStyle;

export const isScheduleActive = (status: string | null | undefined): boolean => {
  if (!status) return false;
  return !TERMINAL_SCHEDULE_STATUSES.has(status.toLowerCase().trim());
};

/** Session rows in these statuses are "closed" for that slot — same UX as cancel: offer Book/Invite, not Reschedule/Cancel/Called Off. */
const RE_BOOKABLE_SCHEDULE_STATUSES = new Set([
  'cancelled', 'canceled', 'no show', 'no-show', 'no_show', 'noshow',
  'called off', 'called_off', 'calledoff',
  'overbooked',
]);

/**
 * A payment has already been asked for on this session — pending PM sign-off, or
 * already approved. Two things key off it: the status editor locks the session
 * (correcting the status now would contradict a payment already in flight), and
 * the row menu reports the payment instead of offering to request it again.
 */
export const isPaymentRequested = (
  paymentStatus: string | null | undefined,
): boolean => {
  const value = String(paymentStatus || '').toLowerCase().trim();
  return value === 'pending' || value === 'approved';
};

export const isScheduleCompleted = (status: string | null | undefined): boolean => {
  if (!status) return false;
  return (status || '').toLowerCase().trim() === 'completed';
};

// OD-1097: statuses that DON'T count toward a participant's per-session-type
// booking cap (they free a slot) — mirrors countCappedSessionsForSessionType
// in data-collector-be/src/routes/schedules.js exactly. Deliberately does NOT
// include 'completed': a completed session still uses one of the
// participant's N allocated sessions for this type.
const NON_COUNTING_SESSION_STATUSES = new Set([
  'cancelled', 'canceled', 'no show', 'no-show', 'no_show',
  'called off', 'called_off', 'calledoff', 'disqualified', 'overbooked', 'blocked',
  'reschedule',
]);

/** True when a schedule counts toward a participant's per-session-type booking cap (OD-1097) — mirrors NON_COUNTING_SESSION_STATUSES above. */
export const isScheduleCountedTowardSessionTypeCap = (status: string | null | undefined): boolean =>
  !NON_COUNTING_SESSION_STATUSES.has(String(status || '').toLowerCase().trim());

/**
 * OD-1111: whether a moderator-relevant schedule is an exact match on the
 * session type + slot being assigned — not just an overlap. Only these are
 * cap-eligible for concurrent moderator bookings; anything else keeps
 * blocking via the legacy one-moderator-one-slot exclusivity/overlap rule.
 * Shared by PMBookSession.tsx and Scheduler.moderatorOptions.ts so the two
 * "is this moderator free for this slot" pickers can't drift apart.
 */
export const isExactSlotSameSessionType = (
  schedule: { session_type_id?: unknown; start_time?: string; end_time?: string } | null | undefined,
  slotStart: Date,
  slotEnd: Date,
  sessionTypeId: string | null,
): boolean => {
  if (!sessionTypeId || !schedule?.session_type_id) return false;
  if (String(schedule.session_type_id) !== String(sessionTypeId)) return false;
  if (!schedule.start_time || !schedule.end_time) return false;
  return (
    new Date(schedule.start_time).getTime() === slotStart.getTime() &&
    new Date(schedule.end_time).getTime() === slotEnd.getTime()
  );
};

export const getLatestScheduleForSessionType = (
  schedules: Array<{ id?: string; session_type_id?: string; title?: string; status?: string; start_time?: string; created_at?: string }>,
  sessionTypeIdCheck: string | undefined,
  sessionTypeName: string | undefined
): typeof schedules[0] | undefined => {
  const matches = schedules.filter((s) => {
    const matchesType =
      (s.session_type_id && sessionTypeIdCheck && String(s.session_type_id) === String(sessionTypeIdCheck)) ||
      (s.title && sessionTypeName && String(s.title).toLowerCase() === String(sessionTypeName).toLowerCase());
    return matchesType;
  });
  if (matches.length === 0) return undefined;
  const sorted = [...matches].sort((a, b) => {
    const aTime = new Date(a.created_at || a.start_time || 0).getTime();
    const bTime = new Date(b.created_at || b.start_time || 0).getTime();
    return bTime - aTime;
  });
  return sorted[0];
};

/** Schedules for the same session type (match by session_type_id and/or title). Used to merge session notes across rebooks/overbooks. */
export const filterSchedulesForSessionType = <
  T extends { session_type_id?: string; title?: string },
>(
  schedules: T[],
  sessionTypeId: string | undefined,
  sessionTitle: string | undefined,
): T[] => {
  return schedules.filter((s) => {
    const matchId =
      Boolean(
        s.session_type_id &&
          sessionTypeId &&
          String(s.session_type_id) === String(sessionTypeId),
      );
    const matchTitle =
      Boolean(
        s.title &&
          sessionTitle &&
          String(s.title).toLowerCase() === String(sessionTitle).toLowerCase(),
      );
    return matchId || matchTitle;
  });
};

export const shouldShowBook = (
  schedule: { status?: string; payment_status?: string } | null | undefined
): boolean => {
  if (!schedule) return true;
  const statusLower = (schedule.status || '').toLowerCase().trim();
  if (statusLower === 'completed') return false;
  if (RE_BOOKABLE_SCHEDULE_STATUSES.has(statusLower)) return true;
  return false;
};

export const HIDDEN_FROM_DROPDOWN_STATUSES = [
  SCHEDULE_STATUS.AVAILABLE,
  SCHEDULE_STATUS.SCHEDULED,
  SCHEDULE_STATUS.RESCHEDULED,
] as const;

export const shouldFetchSession = (status: string | null | undefined): boolean => {
  if (!status) return false;
  const statusLower = status.toLowerCase();
  return (SESSION_FETCH_STATUSES as readonly string[]).includes(statusLower);
};

export const isScheduledLikeStatus = (status: string | null | undefined): boolean => {
  if (!status) return false;
  const statusLower = status.toLowerCase();
  return (SCHEDULED_LIKE_STATUSES as readonly string[]).includes(statusLower);
};

export const shouldHideFromDropdown = (status: string | null | undefined): boolean => {
  if (!status) return false;
  const statusLower = status.toLowerCase();
  return (HIDDEN_FROM_DROPDOWN_STATUSES as readonly string[]).includes(statusLower);
};

export const shouldShowRescheduleButton = (
  status: string | null | undefined,
  sessionStartTime?: string | null,
  sessionStatus?: string | null
): boolean => {
  if (!status) return false;
  
  if (sessionStatus) {
    const sessionStatusLower = sessionStatus.toLowerCase();
    if (sessionStatusLower === 'in progress' || sessionStatusLower === 'in_progress') {
      return false;
    }
  }
  
  const statusLower = status.toLowerCase();
  const isScheduled = statusLower === SCHEDULE_STATUS.SCHEDULED;
  const isReschedule = statusLower === SCHEDULE_STATUS.RESCHEDULE;
  const isRescheduled = statusLower === SCHEDULE_STATUS.RESCHEDULED;
  
  if (isReschedule || isRescheduled) {
    return true;
  }
  
  if (isScheduled && sessionStartTime) {
    const sessionStart = new Date(sessionStartTime);
    const now = new Date();
    return sessionStart > now;
  }
  
  return isScheduled && !sessionStartTime;
};

export { getStatusColor, formatStatusDisplay } from '@/shared/lib/participant-schedule-status-display';

const mapBlockedOrOverbookedStatus = (
  status: string,
  scheduleStatusLower: string,
): string | null => {
  if (status === 'disqualified' || scheduleStatusLower === 'disqualified') {
    return 'Disqualified';
  }
  if (status === 'blocked' || scheduleStatusLower === 'blocked') {
    return 'Do Not Hire';
  }
  if (scheduleStatusLower === SCHEDULE_STATUS.RESCHEDULE) return 'Pending Reschedule';
  if (
    status === 'max_overbooks_reached' ||
    scheduleStatusLower === 'max_overbooks_reached' ||
    status === 'overbooked' ||
    scheduleStatusLower === 'overbooked'
  ) {
    return 'Overbooked';
  }
  return null;
};

const mapSessionLifecycleStatus = (
  status: string,
  scheduleStatusLower: string,
): string | null => {
  if (status === 'cancelled' || scheduleStatusLower === 'cancelled') {
    return 'Cancelled';
  }
  if (status === 'no_show' || scheduleStatusLower === 'no_show') {
    return 'No Show';
  }
  if (status === 'in_progress' || scheduleStatusLower === 'in_progress') {
    return 'In Progress';
  }
  if (status === 'active' || scheduleStatusLower === 'active') {
    return 'Scheduled';
  }
  if (
    status === 'completed' ||
    status === 'participated' ||
    scheduleStatusLower === 'completed'
  ) {
    return 'Completed';
  }
  if (status === 'partially_completed' || status === 'partially completed') {
    return 'Partially Completed';
  }
  return null;
};

const mapDropoutOrCalledOffStatus = (
  status: string,
  scheduleStatusLower: string,
): string | null => {
  if (status === 'declined' || status === 'withdrawn' || status === 'dropout') {
    return 'Dropout';
  }
  if (status === 'called_off' || status === 'called off') return 'Called off';
  if (
    scheduleStatusLower === 'called_off' ||
    scheduleStatusLower === 'called off'
  ) {
    return 'Called off';
  }
  return null;
};

const mapScheduleStatusFallback = (
  status: string,
  scheduleStatus: string | undefined,
  scheduleStatusLower: string,
  scheduleCount: number,
  assignedSchedulerId?: string,
): string => {
  if (isScheduledLikeStatus(scheduleStatus)) return 'Scheduled';
  if (scheduleStatusLower === 'contacting') return 'Scheduling';
  if (
    scheduleStatusLower === 'unreachable' ||
    scheduleStatusLower === 'dropout'
  ) {
    return 'Dropout';
  }
  if (scheduleStatusLower === 'available') return 'Endorsed';
  if (scheduleStatusLower === 'endorsed' || scheduleStatusLower === '') {
    return assignedSchedulerId ? 'Scheduling' : 'Endorsed';
  }
  if (scheduleCount > 0) return 'Scheduled';
  if (status === 'confirmed') return 'Scheduled';
  return 'Not Scheduled';
};

export const mapBackendStatusToUI = (
  backendStatus: string,
  scheduleCount: number,
  scheduleStatus?: string,
  assignedSchedulerId?: string,
): string => {
  const status = backendStatus.toLowerCase();
  const scheduleStatusLower = (scheduleStatus || '').toLowerCase();
  return (
    mapBlockedOrOverbookedStatus(status, scheduleStatusLower) ??
    mapSessionLifecycleStatus(status, scheduleStatusLower) ??
    mapDropoutOrCalledOffStatus(status, scheduleStatusLower) ??
    mapScheduleStatusFallback(
      status,
      scheduleStatus,
      scheduleStatusLower,
      scheduleCount,
      assignedSchedulerId,
    )
  );
};

export const getStatusBadgeVariant = (status: string): string => {
  switch (status) {
    case 'Endorsed':
      return 'bg-purple-100 text-purple-800';
    case 'Scheduling':
      return 'bg-yellow-100 text-yellow-800';
    case 'Scheduled':
      return 'bg-blue-100 text-blue-800';
    case 'Cancelled':
      return 'bg-gray-200 text-gray-800';
    case 'No Show':
      return 'bg-red-50 text-red-700';
    case 'In Progress':
      return 'bg-indigo-50 text-indigo-800';
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Partially Completed':
      return 'bg-orange-100 text-orange-800';
    case 'Dropout':
      return 'bg-red-100 text-red-800';
    case 'Called off':
      return 'bg-red-100 text-red-800';
    case 'Not Scheduled':
      return 'bg-gray-100 text-gray-800';
    case 'Disqualified':
      return 'bg-stone-200 text-stone-900';
    case 'Do not hire':
      return 'bg-stone-200 text-stone-900';
    case 'Blocked':
      return 'bg-stone-200 text-stone-900';
    case 'Overbooked':
      return 'bg-amber-100 text-amber-900';
    case 'Reschedule':
    case 'Pending Reschedule':
      return 'bg-orange-100 text-orange-900';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
};

const isParticipantExcludedFromStatusCounts = (
  scheduleStatus: string,
  backendStatus: string,
): boolean =>
  scheduleStatus === 'disqualified' ||
  backendStatus === 'disqualified' ||
  scheduleStatus === 'blocked' ||
  backendStatus === 'blocked' ||
  scheduleStatus === 'overbooked' ||
  backendStatus === 'overbooked' ||
  scheduleStatus === 'max_overbooks_reached' ||
  backendStatus === 'max_overbooks_reached';

const classifyParticipantStatusCount = (scheduleStatus: string): keyof StatusCounts => {
  if (scheduleStatus === SCHEDULE_STATUS.ENDORSED) return 'endorsed';
  if (scheduleStatus === SCHEDULE_STATUS.CONTACTING) return 'contacting';
  if (isScheduledLikeStatus(scheduleStatus)) return 'scheduled';
  if (scheduleStatus === SCHEDULE_STATUS.UNREACHABLE) return 'unreachable';
  if (scheduleStatus === SCHEDULE_STATUS.AVAILABLE) return 'endorsed';
  if (scheduleStatus === SCHEDULE_STATUS.DROPOUT) return 'dropout';
  if (scheduleStatus === SCHEDULE_STATUS.RESCHEDULE) return 'reschedule';
  if (scheduleStatus === SCHEDULE_STATUS.CALLED_OFF || scheduleStatus === 'called off') return 'called_off';
  return 'endorsed';
};

export const calculateStatusCounts = (participants: Participant[]): StatusCounts => {
  const counts: StatusCounts = {
    endorsed: 0,
    contacting: 0,
    scheduled: 0,
    unreachable: 0,
    dropout: 0,
    reschedule: 0,
    called_off: 0,
  };

  participants.forEach((p) => {
    const scheduleStatus = (p.schedule_status || 'endorsed').toLowerCase();
    const backendStatus = (p.status || '').toLowerCase();

    if (isParticipantExcludedFromStatusCounts(scheduleStatus, backendStatus)) {
      return;
    }

    counts[classifyParticipantStatusCount(scheduleStatus)]++;
  });

  return counts;
};

/**
 * Canonical session-status key for a raw status string. Known spelling/
 * synonym variants collapse to one shared key (e.g. 'no show'/'no_show'/
 * 'noshow'); anything else that isn't an "empty" sentinel (isSessionStatusEmpty
 * — blank, dash placeholders, literal "to be scheduled" text) is kept as its
 * OWN canonical key instead of being dropped, so a brand new status value
 * stays countable/filterable/matchable under its own name rather than
 * silently vanishing (KPI tallies still only track the curated
 * `SessionStatusCounts` keys — see calculateSessionStatusCounts's `key in
 * counts` guard — but filtering/matching must not lose the row). Mirrors
 * normalizeSessionStatus in data-collector-be/src/services/schedulerQueue.js.
 */
const normalizeCoreSessionStatus = (s: string): string | null => {
  if (s === 'scheduled' || s === 'not_started' || s === 'not started' || s === 'notstarted' || s === 'active' || s === 'booked') return 'scheduled';
  if (s === 'in_progress' || s === 'in progress' || s === 'inprogress') return 'in_progress';
  if (s === 'completed') return 'completed';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'called_off' || s === 'called off' || s === 'calledoff') return 'called_off';
  if (s === 'no_show' || s === 'no show' || s === 'noshow') return 'no_show';
  return null;
};

const normalizeExtendedSessionStatus = (s: string): string | null => {
  if (s === 'reschedule') return 'reschedule';
  if (s === 'rescheduled') return 'rescheduled';
  if (s === 'partially_completed' || s === 'partially completed') return 'partially_completed';
  if (s === 'disqualified') return 'disqualified';
  if (s === 'blocked') return 'blocked';
  if (s === 'overbooked') return 'overbooked';
  return null;
};

export const normalizeSessionStatus = (status: string | null | undefined): string | null => {
  if (!status) return null;
  const s = status.toLowerCase().trim().replace(/\s+/g, ' ').replace(/-/g, '_');
  const core = normalizeCoreSessionStatus(s);
  if (core) return core;
  const extended = normalizeExtendedSessionStatus(s);
  if (extended) return extended;
  if (isSessionStatusEmpty(status)) return null;
  return s.replace(/\s+/g, '_');
};

/**
 * The Booked Sessions calendar view shows only currently active
 * bookings — sessions in the "Scheduled" or "Rescheduled" status. Completed,
 * cancelled, no-show, called-off, disqualified and every other status are
 * hidden so the calendar stays clean and actionable. Reuses
 * normalizeSessionStatus so scheduled-equivalent backend values
 * (not_started / active / booked) are treated as "Scheduled" too.
 */
export const isCalendarVisibleStatus = (
  status: string | null | undefined,
): boolean => {
  const normalized = normalizeSessionStatus(status);
  return normalized === 'scheduled' || normalized === 'rescheduled';
};

interface ScheduleForCount {
  participant_id?: string | number;
  session_type_id?: string | number;
  title?: string;
  status?: string;
  created_at?: string;
  start_time?: string;
}

type SessionGroupable = {
  participant_id?: string | number;
  session_type_id?: string | number;
  title?: string;
};

const buildSessionTypeGroupKey = (
  schedules: SessionGroupable[],
): ((s: SessionGroupable) => string) => {
  const typeIdByTitle = new Map<string, string>();
  for (const s of schedules) {
    const title = String(s.title ?? '').trim().toLowerCase();
    const id = s.session_type_id != null ? String(s.session_type_id) : '';
    if (title && id && !typeIdByTitle.has(title)) typeIdByTitle.set(title, id);
  }
  return (s: SessionGroupable) => {
    const pid = String(s.participant_id ?? '');
    const title = String(s.title ?? '').trim().toLowerCase();
    const id = s.session_type_id != null ? String(s.session_type_id) : '';
    const canonical = id || (title ? (typeIdByTitle.get(title) ?? title) : '');
    return `${pid}|${canonical}`;
  };
};

export const calculateSessionStatusCounts = (schedules: ScheduleForCount[]): SessionStatusCounts => {
  const counts: SessionStatusCounts = {
    scheduled: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    called_off: 0,
    no_show: 0,
    reschedule: 0,
    rescheduled: 0,
    partially_completed: 0,
    disqualified: 0,
    blocked: 0,
    overbooked: 0,
  };

  const groupKey = buildSessionTypeGroupKey(schedules);
  const groups = new Map<string, ScheduleForCount[]>();
  schedules.forEach((s) => {
    const k = groupKey(s);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(s);
  });

  groups.forEach((group) => {
    const sorted = [...group].sort((a, b) => {
      const aTime = new Date(a.created_at || a.start_time || 0).getTime();
      const bTime = new Date(b.created_at || b.start_time || 0).getTime();
      return bTime - aTime;
    });
    const latest = sorted[0];
    const key = normalizeSessionStatus(latest?.status);
    if (key && key in counts) {
      (counts as unknown as Record<string, number>)[key]++;
    }
  });

  return counts;
};

/** Matches ParticipantRow `isParticipantBlocked` — KPI "Do Not Hire" should count participants, not latest session rows. */
export function countParticipantsDoNotHire(
  participants: Array<Pick<Participant, 'schedule_status' | 'status'>>,
): number {
  return participants.filter((p) => {
    const ss = String(p.schedule_status || '').toLowerCase().trim();
    const st = String(p.status || '').toLowerCase().trim();
    return ss === 'blocked' || st === 'blocked';
  }).length;
}

export interface ScheduleForFilter {
  participant_id?: string | number;
  session_type_id?: string | number;
  /** Name of linked schedule_session_type row (schedules list API). */
  session_type_template_name?: string;
  /** Onsite / Offsite from joined schedule_session_type.session_type (matches expanded grid Session Type column). */
  session_template_location_type?: string;
  /** Display name of the session instance (not the session type). */
  title?: string;
  /** Fallback label used when title is empty (matches BookedSessions / API). */
  meeting_type?: string;
  /** Canonical session type label from API (e.g. Onsite / Offsite). */
  session_type?: string;
  session_type_name?: string;
  status?: string;
  created_at?: string;
  start_time?: string;
  parent_session_id?: string | null;
}

/** Normalize session type id for Map lookup (UUID case / whitespace). */
export function normalizeSessionTypeIdKey(id: unknown): string {
  return safeString(id).trim().toLowerCase();
}

/** Map session type id_guid/id → display name for Scheduler filters and schedule labels. */
export function buildSessionTypeIdToNameMap(
  sessionTypes:
    | Array<{id_guid?: string; id?: string; name?: string}>
    | undefined
    | null,
): Map<string, string> {
  const m = new Map<string, string>();
  (sessionTypes || []).forEach((st) => {
    const id = String(st.id_guid ?? st.id ?? '').trim();
    if (!id) return;
    const name = String(st.name ?? '').trim();
    m.set(normalizeSessionTypeIdKey(id), name || id);
  });
  return m;
}

/** Booking instance label only (title / meeting_type — not template name). */
export function scheduleInstanceDisplayLabel(s: ScheduleForFilter): string {
  const row = s as Record<string, unknown>;
  const title = safeString(s.title ?? row.task_info).trim();
  if (title) return title;
  const meeting = safeString(s.meeting_type ?? row.meetingType).trim();
  if (meeting) return meeting;
  return '';
}

/** Expanded grid "Session Type" column: Onsite or Offsite (schedule_session_type.session_type). */
export function formatSessionLocationTypeLabel(
  sessionTypeRaw?: string | null,
): string {
  const raw = String(sessionTypeRaw || 'onsite').toLowerCase();
  return raw === 'offsite' ? 'Offsite' : 'Onsite';
}

/**
 * Expanded grid "Session Name" column for a schedule row: title → meeting_type → template name.
 */
export function scheduleGridSessionNameLabel(s: ScheduleForFilter): string {
  const inst = scheduleInstanceDisplayLabel(s);
  if (inst) return inst;
  return String(s.session_type_template_name || '').trim();
}

/**
 * Expanded grid Session Name when schedule may omit joined template fields (uses session type name).
 */
export function scheduleExpandedGridSessionName(
  schedule:
    | {
        title?: string;
        meeting_type?: string;
        session_type_template_name?: string;
      }
    | undefined,
  sessionType: {name?: string},
): string {
  const title = String(schedule?.title ?? '').trim();
  if (title) return title;
  const meeting = String(schedule?.meeting_type ?? '').trim();
  if (meeting) return meeting;
  const tmpl = String(schedule?.session_type_template_name ?? '').trim();
  if (tmpl) return tmpl;
  return String(sessionType?.name ?? '').trim();
}

/**
 * Primary + secondary moderator name(s) on a schedule row. `moderator_name`/
 * `secondary_moderator_name` are comma-joined DISPLAY strings derived from the
 * `*_moderator_names` arrays (see attachScheduleModerators on the backend) —
 * prefer the arrays so a multi-moderator session isn't matched against a
 * composite "A, B" string; fall back to the joined string only when a row
 * shape doesn't carry the arrays at all.
 */
export function scheduleModeratorNames(
  schedule:
    | {
        primary_moderator_names?: unknown;
        secondary_moderator_names?: unknown;
        moderator_name?: string | null;
        secondary_moderator_name?: string | null;
      }
    | undefined,
): {primary: string[]; secondary: string[]} {
  const clean = (arr: unknown[]): string[] =>
    arr.map((v) => String(v ?? '').trim()).filter(Boolean);
  const primary = Array.isArray(schedule?.primary_moderator_names)
    ? clean(schedule.primary_moderator_names as unknown[])
    : (() => {
        const v = String(schedule?.moderator_name ?? '').trim();
        return v ? [v] : [];
      })();
  const secondary = Array.isArray(schedule?.secondary_moderator_names)
    ? clean(schedule.secondary_moderator_names as unknown[])
    : (() => {
        const v = String(schedule?.secondary_moderator_name ?? '').trim();
        return v ? [v] : [];
      })();
  return {primary, secondary};
}

const isPrimaryScheduleRow = (s: ScheduleForFilter) =>
  s.parent_session_id == null || s.parent_session_id === undefined || s.parent_session_id === '';

export const getLatestSessionStatusesByParticipantId = (
  schedules: ScheduleForFilter[]
): Map<string, Set<string>> => {
  const primarySchedules = schedules.filter(isPrimaryScheduleRow);
  const groupKey = buildSessionTypeGroupKey(primarySchedules);
  const groups = new Map<string, ScheduleForFilter[]>();
  primarySchedules.forEach((s) => {
    const k = groupKey(s);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(s);
  });

  const result = new Map<string, Set<string>>();
  groups.forEach((group) => {
    const sorted = [...group].sort((a, b) => {
      const aTime = new Date(a.created_at || a.start_time || 0).getTime();
      const bTime = new Date(b.created_at || b.start_time || 0).getTime();
      return bTime - aTime;
    });
    const latest = sorted[0];
    const pid = String(latest?.participant_id ?? '');
    if (!pid) return;
    const key = normalizeSessionStatus(latest?.status);
    if (!result.has(pid)) result.set(pid, new Set());
    if (key) {
      result.get(pid)!.add(key);
    } else if (isSessionStatusEmpty(latest?.status)) {
      result.get(pid)!.add('to_be_scheduled');
    }
  });
  return result;
};

/** True when a schedule row is this participant's row for the given session type (by id or title). */
const scheduleMatchesParticipantSessionType = (
  s: ScheduleForFilter,
  pid: string,
  typeId: string | undefined,
  typeName: string,
): boolean => {
  if (String(s.participant_id) !== pid) return false;
  const byId =
    typeId != null &&
    typeId !== '' &&
    s.session_type_id != null &&
    String(s.session_type_id) === String(typeId);
  const byName =
    Boolean(typeName) &&
    Boolean(s.title) &&
    String(s.title).toLowerCase() === typeName;
  return byId || byName;
};

/** Add the synthetic 'to_be_scheduled' status for a participant. */
const markToBeScheduled = (base: Map<string, Set<string>>, pid: string): void => {
  if (!base.has(pid)) base.set(pid, new Set());
  base.get(pid)!.add('to_be_scheduled');
};

/** True when any eligible session type has no primary schedule or an empty latest status. */
const hasUnscheduledEligibleType = (
  pid: string,
  eligible: Array<{ id_guid?: string; id?: string; name?: string }>,
  primary: ScheduleForFilter[],
): boolean => {
  for (const st of eligible) {
    const typeId = st.id_guid ?? st.id;
    const typeName = (st.name || '').toLowerCase();
    const matches = primary.filter((s) =>
      scheduleMatchesParticipantSessionType(s, pid, typeId, typeName),
    );
    if (matches.length === 0) return true;
    const latest = [...matches].sort((a, b) => {
      const aTime = new Date(a.created_at || a.start_time || 0).getTime();
      const bTime = new Date(b.created_at || b.start_time || 0).getTime();
      return bTime - aTime;
    })[0];
    if (isSessionStatusEmpty(latest.status)) return true;
  }
  return false;
};

/**
 * Marks participants who have at least one eligible session type with no primary schedule
 * or with an empty / “to be scheduled” session status (matches Scheduler grid + filter).
 */
export const mergeToBeScheduledIntoLatestSessionStatusMap = (
  base: Map<string, Set<string>>,
  participants: Array<{ id: string | number }>,
  eligibleSessionTypesByParticipantId: Record<
    string,
    Array<{ id_guid?: string; id?: string; name?: string }>
  >,
  schedules: ScheduleForFilter[],
): void => {
  const primary = schedules.filter(isPrimaryScheduleRow);
  for (const p of participants) {
    const pid = String(p.id);
    const eligible = eligibleSessionTypesByParticipantId[pid] || [];
    if (eligible.length === 0) continue;
    if (hasUnscheduledEligibleType(pid, eligible, primary)) {
      markToBeScheduled(base, pid);
    }
  }
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Session statuses that count as "upcoming" for the Upcoming tab (Scheduled or Rescheduled only). */
const UPCOMING_TAB_STATUSES = new Set<string>(['scheduled', 'rescheduled']);

/**
 * Returns participant IDs that have at least one schedule with start_time in the next 24 hours
 * and status 'Scheduled' or 'Rescheduled'.
 */
export const getParticipantIdsWithSessionsInNext24h = (
  schedules: Array<{ participant_id?: string | number; start_time?: string; status?: string }>,
  windowMs: number = TWENTY_FOUR_HOURS_MS
): Set<string> => {
  const now = Date.now();
  const end = now + windowMs;
  const result = new Set<string>();
  for (const s of schedules) {
    const pid = s.participant_id != null ? String(s.participant_id) : '';
    if (!pid) continue;
    const key = normalizeSessionStatus(s.status);
    if (!key || !UPCOMING_TAB_STATUSES.has(key)) continue;
    const startTime = s.start_time ? new Date(s.start_time).getTime() : NaN;
    if (Number.isNaN(startTime)) continue;
    if (startTime >= now && startTime <= end) result.add(pid);
  }
  return result;
};

/**
 * Participant has a session row or eligible session type whose **location mode** matches
 * (Onsite / Offsite — same as expanded grid "Session Type" column, not template name).
 */
export const participantHasSessionTypes = (
  participantId: string,
  schedules: ScheduleForFilter[],
  sessionTypesToMatch: Set<string>,
  eligibleSessionTypes?: Array<{session_type?: string}>,
): boolean => {
  if (sessionTypesToMatch.size === 0) return true;
  const pid = String(participantId);
  const wanted = new Set(
    [...sessionTypesToMatch].map((x) => formatSessionLocationTypeLabel(x)),
  );

  const fromSchedules = schedules.some((s) => {
    if (String(s.participant_id) !== pid) return false;
    const raw =
      s.session_template_location_type ??
      (s as Record<string, unknown>).session_template_location_type;
    const label = formatSessionLocationTypeLabel(
      raw != null ? safeString(raw) : undefined,
    );
    return wanted.has(label);
  });
  if (fromSchedules) return true;

  return (eligibleSessionTypes || []).some((st) => {
    const label = formatSessionLocationTypeLabel(st?.session_type);
    return wanted.has(label);
  });
};

/**
 * Display label for a schedule row (expanded grid, exports): instance fields, then template/API fallbacks.
 */
export const scheduleDisplaySessionName = (
  s: ScheduleForFilter,
  sessionTypeIdToName?: Map<string, string>,
): string => {
  const instance = scheduleInstanceDisplayLabel(s);
  if (instance) return instance;
  const template = String(s.session_type_template_name || '').trim();
  if (template) return template;
  const row = s as Record<string, unknown>;
  const typeIdRaw =
    s.session_type_id ?? row.sessionTypeId ?? row.session_type_id;
  const typeId =
    typeIdRaw != null && typeIdRaw !== '' ? safeString(typeIdRaw).trim() : '';
  if (typeId && sessionTypeIdToName?.size) {
    const key = normalizeSessionTypeIdKey(typeId);
    if (sessionTypeIdToName.has(key)) {
      const resolved = sessionTypeIdToName.get(key);
      if (resolved) return resolved;
    }
  }
  return String(s.session_type_name || '').trim();
};

/**
 * Participant has a schedule or eligible session type whose **Session Name** column value matches
 * (title → meeting_type → template name — same as expanded grid).
 */
export const participantHasSessionNames = (
  participantId: string,
  schedules: ScheduleForFilter[],
  names: Set<string>,
  eligibleSessionTypes?: Array<{name?: string}>,
): boolean => {
  if (names.size === 0) return true;
  const pid = String(participantId);

  const hit = schedules.some((s) => {
    if (String(s.participant_id) !== pid) return false;
    const n = scheduleGridSessionNameLabel(s);
    return n !== '' && names.has(n);
  });
  if (hit) return true;

  return (eligibleSessionTypes || []).some((st) => {
    const n = String(st.name ?? '').trim();
    return n !== '' && names.has(n);
  });
};

/** Latest primary schedule per eligible session type has no primary moderator. */
export const participantHasUnassignedModeratorSession = (
  participantId: string,
  eligibleSessionTypes: Array<{id_guid?: string; id?: string; name?: string}>,
  schedules: Array<ScheduleForFilter & {moderator_id?: string | null}>,
): boolean => {
  const pid = String(participantId);
  const primary = schedules.filter(isPrimaryScheduleRow);
  const isTerminalStatus = (status: unknown) => {
    const s = safeString(status)
      .toLowerCase()
      .trim();
    return (
      s === 'cancelled' ||
      s === 'canceled' ||
      s === 'completed' ||
      s === 'no show' ||
      s === 'called off'
    );
  };

  for (const st of eligibleSessionTypes) {
    const typeId = st.id_guid ?? st.id;
    const typeName = (st.name || '').toLowerCase();
    const matches = primary.filter(
      (s) =>
        String(s.participant_id) === pid &&
        ((typeId != null &&
          typeId !== '' &&
          s.session_type_id != null &&
          String(s.session_type_id) === String(typeId)) ||
          (Boolean(typeName) &&
            s.title &&
            String(s.title).toLowerCase() === typeName)),
    );
    const activeMatches = matches.filter((s) => !isTerminalStatus(s.status));
    if (activeMatches.length === 0) {
      continue;
    }
    const sorted = [...activeMatches].sort((a, b) => {
      const aTime = new Date(a.created_at || a.start_time || 0).getTime();
      const bTime = new Date(b.created_at || b.start_time || 0).getTime();
      return bTime - aTime;
    });
    const latest = sorted[0];
    const mod = (latest as {moderator_id?: string | null}).moderator_id;
    if (mod == null || String(mod).trim() === '') {
      return true;
    }
  }
  return false;
};

// --- Contact log export: Notes & History cell (matches Session contact ActivityTimeline) ---

export function filterHistoryBySessionType(
  entries: unknown[],
  sessionTypeId: string | null | undefined,
): Record<string, unknown>[] {
  if (sessionTypeId == null || String(sessionTypeId).trim() === '') {
    return [];
  }
  const sid = String(sessionTypeId);
  return (entries as Record<string, unknown>[]).filter((entry) => {
    const meta = entry.metadata as {session_type_id?: string} | undefined;
    const st = meta?.session_type_id;
    return st != null && String(st) === sid;
  });
}

/** Single-line text safe for Excel CSV cells (no raw newlines). */
function sanitizeHistoryExportLine(value: string): string {
  return String(value)
    .replace(/\r\n/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatHistoryTimestampLocal(createdAt: string | undefined): string {
  if (!createdAt) return 'N/A';
  const date = new Date(createdAt);
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const formattedTime = formatDisplayTimeWithSeconds(date);
  // ASCII-only punctuation so Excel (Windows) does not mangle UTF-8 bullets/dashes.
  return `${formattedDate} ${formattedTime}`;
}

/** Title-case an underscore-delimited token (e.g. "no_answer" -> "No Answer"). */
export function titleCaseUnderscored(value: string): string {
  return value
    .split('_')
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Stringify a primitive; objects (and null/undefined) yield '' rather than '[object Object]'. */
function primitiveToString(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : '';
}

const CONTACT_OUTCOME_LABELS: Record<string, string> = {
  email: 'Email',
  text: 'Text',
  call_answered: 'Call - Answered',
  call_not_answered: 'Call - Not Answered',
  call_voicemail: 'Call - Voicemail',
  wrong_number: 'Wrong Number',
  not_interested: 'Not Interested',
  answered: 'Answered',
  no_answer: 'No Answer',
  voicemail: 'Voicemail',
};

/** Format a contact-attempt entry as "Contact attempt #N - Outcome". */
function formatContactAttempt(metadata: Record<string, unknown>): string {
  const outcome = primitiveToString(metadata.outcome);
  const attemptNumber = primitiveToString(metadata.attempt_number);
  const outcomeFormatted = CONTACT_OUTCOME_LABELS[outcome] || titleCaseUnderscored(outcome);
  return `Contact attempt #${attemptNumber} - ${outcomeFormatted}`;
}

/** Resolve the human-readable description line for a history export entry. */
function resolveHistoryExportDescription(
  entry: Record<string, unknown>,
  metadata: Record<string, unknown> | undefined,
  isContactAttempt: boolean,
  isStatusChange: boolean,
  isNote: boolean,
  description: string,
): string {
  if (isContactAttempt && metadata?.attempt_number && metadata?.outcome) {
    return formatContactAttempt(metadata);
  }
  if (isStatusChange && metadata) {
    const newStatus = metadata.new_schedule_status as string | undefined;
    if (newStatus) {
      const statusFormatted = titleCaseUnderscored(newStatus);
      const display =
        statusFormatted.toLowerCase() === 'reschedule'
          ? 'Pending Reschedule'
          : statusFormatted;
      return `Participant Status changed to ${display}`;
    }
    if (!description || description.trim() === '') return 'Status updated';
    return description;
  }
  if (isNote) {
    return safeString(
      entry.details || entry.action_description || entry.action || 'Note added',
    )
      .replace(/Project Status\s+to\s+[^,]+(?:,\s*)?/gi, '')
      .replace(/,\s*Project Status\s+to\s+[^,]+/gi, '')
      .trim();
  }
  return description;
}

/** The leading label for a history export block (Notes / Attempt / Status / Entry). */
function historyExportPrimaryLabel(
  isNote: boolean,
  isContactAttempt: boolean,
  isStatusChange: boolean,
): string {
  if (isNote) return 'Notes';
  if (isContactAttempt) return 'Attempt';
  if (isStatusChange) return 'Status';
  return 'Entry';
}

function formatSingleHistoryBlockForExport(entry: Record<string, unknown>): string {
  const actionType = (entry.action_type as string) || '';
  const actionDescription = safeString(
    entry.action_description || entry.action || '',
  ).toLowerCase();
  const metadata = entry.metadata as Record<string, unknown> | undefined;
  const hasStatusMetadata = Boolean(
    metadata &&
      (metadata.status_type !== undefined ||
        metadata.new_status !== undefined ||
        metadata.new_schedule_status !== undefined ||
        metadata.old_status !== undefined ||
        metadata.old_schedule_status !== undefined),
  );
  const isStatusChange =
    actionType === 'status_change' ||
    hasStatusMetadata ||
    actionDescription.includes('status changed') ||
    actionDescription.includes('status set to') ||
    actionDescription.includes('status updated') ||
    actionDescription.includes('schedule status changed') ||
    actionDescription.includes('participant status changed') ||
    actionDescription.includes('schedule status set to') ||
    actionDescription.includes('participant status set to') ||
    actionDescription.includes('project status') ||
    actionDescription.includes('participant endorsed');
  const isContactAttempt = actionType === 'contact_attempt';
  const isNote = actionType === 'note_added';

  let description = safeString(entry.action_description || entry.action || '');
  description = description
    .replace(/Project\s+Status\s+to\s+[^,]+(?:,\s*)?/gi, '')
    .replace(/,\s*Project\s+Status\s+to\s+[^,]+/gi, '')
    .replace(/Project\s+Status\s+changed\s+to\s+[^,]+(?:,\s*)?/gi, '')
    .replace(/,\s*Project\s+Status\s+changed\s+to\s+[^,]+/gi, '')
    .replace(/Schedule\s+Status/gi, 'Participant Status')
    .replace(/Status\s+updated:\s*,\s*/gi, 'Status updated: ')
    .replace(/Status\s+updated:\s*$/gi, '')
    .replace(/,\s*,/g, ',')
    .replace(/,\s*$/g, '')
    .trim();

  description = resolveHistoryExportDescription(
    entry,
    metadata,
    isContactAttempt,
    isStatusChange,
    isNote,
    description,
  );

  const detailStr =
    entry.details != null ? safeString(entry.details).trim() : '';
  const descriptionTrimmed = sanitizeHistoryExportLine(description.trim());
  const showOptionalNotes =
    detailStr.length > 0 &&
    !isStatusChange &&
    (isContactAttempt || detailStr !== descriptionTrimmed);

  const primaryText = descriptionTrimmed || 'Activity';
  const detailsLine = showOptionalNotes
    ? sanitizeHistoryExportLine(detailStr)
    : '';
  const when = formatHistoryTimestampLocal(entry.created_at as string | undefined);
  const by = sanitizeHistoryExportLine(
    (entry.created_by_name as string) ||
      (entry.created_by as string) ||
      'Unknown',
  );

  const primaryLabel = historyExportPrimaryLabel(
    isNote,
    isContactAttempt,
    isStatusChange,
  );

  const lines: string[] = [`${primaryLabel}: ${primaryText}`];
  if (detailsLine) {
    lines.push(`Details: ${detailsLine}`);
  }
  lines.push(`When: ${when}`);
  lines.push(`By: ${by}`);
  return lines.join('\n');
}

const NOTES_HISTORY_EXPORT_SEPARATOR =
  '\n----------------------------------------------------------------\n';

export function formatNotesAndHistoryForExport(
  entries: Record<string, unknown>[],
): string {
  if (!entries.length) return '';
  const sorted = [...entries].sort((a, b) => {
    const dateB = new Date((b.created_at as string) || 0).getTime();
    const dateA = new Date((a.created_at as string) || 0).getTime();
    return dateB - dateA;
  });
  return sorted.map((e) => formatSingleHistoryBlockForExport(e)).join(NOTES_HISTORY_EXPORT_SEPARATOR);
}

/** Label shown in Participant Status column / filter (matches grouped filter semantics). */
export function participantScheduleStatusFilterLabel(
  scheduleStatus: string | undefined | null,
  groups: SchedulerParticipantStatusFilterGroup[] = SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
): string {
  const raw = String(scheduleStatus || 'endorsed').toLowerCase().trim();
  const group = groups.find((g) => g.values.some((v) => v === raw));
  if (group) return group.label;
  return displayParticipantScheduleStatus(scheduleStatus || 'endorsed');
}

/** Map stored raw schedule_status values to dialog checkbox labels. */
export function selectedScheduleStatusesToFilterLabels(
  selected: Set<string>,
  groups: SchedulerParticipantStatusFilterGroup[] = SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
): Set<string> {
  const labels = new Set<string>();
  selected.forEach((raw) => {
    const r = String(raw).toLowerCase().trim();
    const group = groups.find((g) => g.values.some((v) => v === r));
    if (group) labels.add(group.label);
    else labels.add(displayParticipantScheduleStatus(raw));
  });
  return labels;
}

/**
 * The Participant Status column shows per-session status chips (To Be
 * Scheduled, Invited, Cancelled, ...) for participants with sessions, so its
 * filter offers those statuses alongside the raw schedule_status ones. A
 * session status is carried in the same schedule_statuses selection with this
 * prefix; the backend matches it against the participant's session statuses.
 */
export const PARTICIPANT_STATUS_SESSION_PREFIX = 'session:';

const sessionStatusLabelForKey = (
  key: string,
  sessionOptions: SchedulerSessionStatusFilterOption[],
): string =>
  sessionOptions.find((o) => o.key === key)?.label ?? titleCaseUnderscored(key);

/** Session-status filter options for the given keys: curated labels first, any other key title-cased. */
export function sessionStatusOptionsForKeys(
  keys: Iterable<string>,
): SchedulerSessionStatusFilterOption[] {
  const present = new Set(keys);
  const curated = SCHEDULER_SESSION_STATUS_FILTER_OPTIONS.filter((o) => present.has(o.key));
  const curatedKeys = new Set(curated.map((o) => o.key));
  const uncurated = [...present]
    .filter((k) => !curatedKeys.has(k))
    .sort((a, b) => a.localeCompare(b))
    .map((k) => ({key: k, label: titleCaseUnderscored(k)}));
  return [...curated, ...uncurated];
}

/** Participant Status filter options: participant-status labels merged with session-status labels. */
export function buildParticipantStatusFilterLabels(
  participantStatusLabels: string[],
  sessionOptions: SchedulerSessionStatusFilterOption[],
): string[] {
  const labels = new Set<string>(participantStatusLabels);
  sessionOptions.forEach((o) => labels.add(o.label));
  return [...labels].sort((a, b) => a.localeCompare(b));
}

/**
 * Participant Status selection (raw schedule_status values plus prefixed
 * session statuses) to dialog checkbox labels. A label present in both lists
 * (e.g. "Cancelled") collapses to one checkbox.
 */
export function participantStatusSelectionToFilterLabels(
  selected: Set<string>,
  sessionOptions: SchedulerSessionStatusFilterOption[] = SCHEDULER_SESSION_STATUS_FILTER_OPTIONS,
  groups: SchedulerParticipantStatusFilterGroup[] = SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
): Set<string> {
  const raw = new Set<string>();
  const labels = new Set<string>();
  selected.forEach((value) => {
    if (value.startsWith(PARTICIPANT_STATUS_SESSION_PREFIX)) {
      labels.add(
        sessionStatusLabelForKey(
          value.slice(PARTICIPANT_STATUS_SESSION_PREFIX.length),
          sessionOptions,
        ),
      );
    } else {
      raw.add(value);
    }
  });
  selectedScheduleStatusesToFilterLabels(raw, groups).forEach((l) => labels.add(l));
  return labels;
}

/**
 * Participant Status dialog labels back to the schedule_statuses selection:
 * raw schedule_status values for participant-status labels, prefixed keys for
 * session-status labels (both when a label is in both lists).
 */
export function participantStatusFilterLabelsToSelection(
  selectedLabels: Set<string>,
  participantStatuses: Array<{label: string; values: string[]}>,
  sessionOptions: SchedulerSessionStatusFilterOption[],
  groups: SchedulerParticipantStatusFilterGroup[] = SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
): Set<string> {
  const values = new Set<string>();
  selectedLabels.forEach((label) => {
    const group = groups.find((g) => g.label === label);
    if (group) group.values.forEach((v) => values.add(v));
    else {
      participantStatuses
        .find((e) => e.label === label)
        ?.values.forEach((v) => values.add(v));
    }
    sessionOptions
      .filter((o) => o.label === label)
      .forEach((o) => values.add(`${PARTICIPANT_STATUS_SESSION_PREFIX}${o.key}`));
  });
  return values;
}

/** Map filter dialog labels back to raw schedule_status values for filtering. */
export function filterLabelsToSelectedScheduleStatuses(
  labels: Set<string>,
  participants: Array<{schedule_status?: string | null}>,
  groups: SchedulerParticipantStatusFilterGroup[] = SCHEDULER_PARTICIPANT_STATUS_FILTER_GROUPS,
): Set<string> {
  const values = new Set<string>();
  labels.forEach((label) => {
    const group = groups.find((g) => g.label === label);
    if (group) {
      group.values.forEach((v) => values.add(v));
      return;
    }
    participants.forEach((p) => {
      if (participantScheduleStatusFilterLabel(p.schedule_status, groups) === label) {
        values.add(String(p.schedule_status || 'endorsed').toLowerCase().trim());
      }
    });
  });
  return values;
}

// --- Shared session-row guards + Session view data shaping ---------------
// Promoted from ParticipantRow.tsx so both the nested session grid and the
// Session view (Scheduler.tsx / SessionView.tsx) key off identical eligibility
// rules instead of forked copies.

export interface SessionTypeLike {
  id_guid?: string;
  id?: string;
  name?: string;
  timezone?: string;
  session_type?: 'onsite' | 'offsite' | string;
  /** OD-1097: when true, a participant may hold more than one session of this type at once. */
  allow_multiple_bookings_per_participant?: boolean;
  /** OD-1097: max concurrent+completed sessions per participant (set only when the flag above is true). */
  max_sessions_per_participant?: number | null;
  cap_participant_sessions_in_window?: boolean;
  window_limit_basis?: 'hours' | 'sessions' | null;
  window_days?: number | null;
  window_capacity?: number | null;
  max_hours_per_day?: number | null;
}

export interface ScheduleLike {
  id?: string;
  session_type_id?: string;
  title?: string;
  meeting_type?: string;
  status?: string;
  moderator_id?: string | null;
  start_time?: string;
  end_time?: string;
  created_at?: string;
  moderator_name?: string;
  secondary_moderator_name?: string;
  payment_status?: string;
  /** How the payment on the session began (payment_requests.source). */
  payment_source?: string | null;
  oneforma_payment_status?: string;
  /** OD-1226 recorded worked time (stamped on check-in / Completed). */
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  /** Moderator/PM correction of the recorded pair; overrides it when both ends are set. */
  adjusted_start_time?: string | null;
  adjusted_end_time?: string | null;
  session_notes?: Array<{ id?: string; body?: string }>;
  location?: string | { name?: string };
  street_address?: string;
  street_address_2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  participant_id?: string | number;
}

/**
 * True when the participant holds an active (unexpired, unused) self-booking
 * invite link for this session type. Backed by `active_invite_session_type_ids`
 * on the participant row (computed live from participant_booking_links — see
 * routes/scheduler.js loadActiveInviteLinksByParticipant).
 */
export const hasActiveInviteForSessionType = (
  participant: Participant,
  st: { id_guid?: string; id?: string },
): boolean => {
  const typeId = st?.id_guid || (st as any)?.id;
  if (!typeId) return false;
  const activeIds: string[] = (participant as any).active_invite_session_type_ids || [];
  return activeIds.includes(String(typeId));
};

/**
 * OD-1139: Total number of times this participant has been (re)invited OR
 * scheduler/PM-led (re)booked for this session type (AC5 counts both
 * channels). Backed by `invite_counts_by_session_type_id` on the participant
 * row.
 */
export const getInviteCountForSessionType = (
  participant: Participant,
  st: { id_guid?: string; id?: string },
): number => {
  const typeId = st?.id_guid || (st as any)?.id;
  if (!typeId) return 0;
  const counts = (participant as any).invite_counts_by_session_type_id || {};
  return Number(counts[String(typeId)] || 0);
};

/**
 * OD-1139: Invite action label — "Invite" before any invite has been sent;
 * "Re-invite" after the first; "Re-invite (n)" from the second onward, where
 * n is the number of invites sent AFTER the first (so Re-invite (1) means 2
 * invites sent in total).
 */
export const getInviteActionLabel = (inviteCount: number): string => {
  if (!inviteCount || inviteCount <= 0) return 'Invite';
  const reinviteCount = inviteCount - 1;
  return reinviteCount === 0 ? 'Re-invite' : `Re-invite (${reinviteCount})`;
};

const resolveSchedulingBlockedTooltip = (
  isParticipantDisqualified: boolean,
  isParticipantBlocked: boolean,
  isParticipantMaxOverbooksReached: boolean,
  isParticipantOverbookCap: boolean,
): string => {
  if (isParticipantDisqualified) return 'Disqualified — cannot schedule.';
  if (isParticipantBlocked) return 'Do not hire — cannot schedule.';
  if (isParticipantMaxOverbooksReached || isParticipantOverbookCap) {
    return `Overbooked ${MAX_PARTICIPANT_OVERBOOKS} times — cannot schedule or assign.`;
  }
  return '';
};

const resolveSchedulingBlockedAriaLabel = (
  isParticipantDisqualified: boolean,
  isParticipantBlocked: boolean,
): string => {
  if (isParticipantDisqualified) return 'Disqualified — cannot schedule';
  if (isParticipantBlocked) return 'Do not hire — cannot schedule';
  return 'Overbook limit — cannot schedule';
};

/** Derive a participant's scheduling-block flags + tooltip/aria copy from its status. */
export const deriveParticipantSchedulingFlags = (participant: any) => {
  const a = String(participant.schedule_status || '').toLowerCase().trim();
  const b = String(participant.status || '').toLowerCase().trim();
  const isParticipantDisqualified = a === 'disqualified' || b === 'disqualified';
  const isParticipantBlocked = a === 'blocked' || b === 'blocked';
  const isParticipantMaxOverbooksReached =
    a === 'max_overbooks_reached' || b === 'max_overbooks_reached';
  const overbookCount = Number((participant as Participant).overbook_count ?? 0);
  const isParticipantOverbookCap = overbookCount >= MAX_PARTICIPANT_OVERBOOKS;
  const participantSchedulingBlocked =
    isParticipantDisqualified ||
    isParticipantBlocked ||
    isParticipantMaxOverbooksReached ||
    isParticipantOverbookCap;
  const schedulingBlockedTooltip = resolveSchedulingBlockedTooltip(
    isParticipantDisqualified,
    isParticipantBlocked,
    isParticipantMaxOverbooksReached,
    isParticipantOverbookCap,
  );
  const canOverbookForMenu = overbookCount < MAX_PARTICIPANT_OVERBOOKS;
  const schedulingBlockedAriaLabel = resolveSchedulingBlockedAriaLabel(
    isParticipantDisqualified,
    isParticipantBlocked,
  );
  return {
    isParticipantDisqualified,
    isParticipantBlocked,
    isParticipantMaxOverbooksReached,
    overbookCount,
    isParticipantOverbookCap,
    participantSchedulingBlocked,
    schedulingBlockedTooltip,
    canOverbookForMenu,
    schedulingBlockedAriaLabel,
  };
};

/** Whether a session's status permits PM book-outcome actions (overbook/disqualify/etc.). */
export const sessionAllowsPmBookOutcomes = (sched?: ScheduleLike) => {
  if (!sched?.status) return false;
  const s = String(sched.status).toLowerCase().replace(/_/g, ' ').trim();
  if (
    ['disqualified', 'blocked', 'overbooked', 'cancelled', 'completed', 'no show', 'called off'].includes(
      s,
    )
  ) {
    return false;
  }
  if (s === 'in progress' || s === 'inprogress') return false;
  return ['scheduled', 'active', 'rescheduled', 'not started', 'booked'].includes(s);
};

/** Aligns with BookedSessions `getAllowedActions` for Mark as Completed. */
export const scheduleAllowsMarkCompleted = (sched?: ScheduleLike) => {
  if (!sched?.status) return false;
  const s = String(sched.status).toLowerCase().replace(/_/g, ' ').trim();
  if (['not started', 'scheduled', 'active', 'rescheduled', 'reschedule'].includes(s)) {
    return true;
  }
  if (s === 'in progress' || s === 'inprogress') return true;
  if (s === 'partially completed') return true;
  return false;
};

export const hasPrimaryModeratorAssigned = (sched?: ScheduleLike): boolean => {
  if (!sched) return false;
  const modId = String(sched.moderator_id ?? '').trim();
  if (modId) return true;
  const modName = String(sched.moderator_name ?? '').trim();
  return modName !== '';
};

/** Cancel is only meaningful when the session can still be cancelled. */
export const sessionAllowsCancelAction = (status: string | undefined) => {
  const s = (status || '').toLowerCase().trim().replace(/_/g, ' ');
  if (s === 'in progress' || s === 'inprogress') return false;
  if (s === 'cancelled' || s === 'canceled') return false;
  if (s === 'completed') return false;
  return true;
};

export const canManageModeratorsForStatus = (status: string | undefined) => {
  const s = String(status || '').toLowerCase().trim().replace(/_/g, ' ');
  return s === 'scheduled' || s === 'active' || s === 'rescheduled';
};

/**
 * True when a booked, still-manageable session (self-booked or manual) has no
 * moderator assigned yet — the condition the Session tab's "Assign moderator"
 * pill and its "Action needed" banner both flag. Does not factor in the
 * isBookingOptionalModeratorEnabled() feature flag; callers AND that in.
 */
export const sessionMissingModerator = (schedule: {status?: string; moderator_name?: string | null} | undefined | null): boolean => {
  if (!schedule) return false;
  if (!canManageModeratorsForStatus(schedule.status)) return false;
  return String(schedule.moderator_name ?? '').trim() === '';
};

/**
 * Total count of sessions missing a moderator across the FULL project schedule
 * pool — not just the currently-loaded/paginated participants, and not
 * narrowed by any Session-view filter (moderator, session status/name/type).
 * Drives the Session tab's "Action needed" banner (AC: alert must reflect the
 * true total and must not depend on pagination or the Moderator filter).
 * Groups by participant + session-type (same key as calculateSessionStatusCounts)
 * and checks only the latest booking per slot, so a rebooked/rescheduled slot's
 * superseded rows aren't double-counted.
 */
export function countSessionsMissingModerator(
  schedules: Array<ScheduleForCount & {moderator_name?: string | null}>,
): number {
  const groupKey = buildSessionTypeGroupKey(schedules);
  const groups = new Map<string, Array<ScheduleForCount & {moderator_name?: string | null}>>();
  schedules.forEach((s) => {
    const k = groupKey(s);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(s);
  });
  let count = 0;
  groups.forEach((group) => {
    const sorted = [...group].sort((a, b) => {
      const aTime = new Date(a.created_at || a.start_time || 0).getTime();
      const bTime = new Date(b.created_at || b.start_time || 0).getTime();
      return bTime - aTime;
    });
    if (sessionMissingModerator(sorted[0])) count += 1;
  });
  return count;
}

/** Bucket -> abbreviated mini-chip label (12 KPI buckets + the 2 synthetic/filter-only ones). */
export const SESSION_BUCKET_SHORT_LABEL: Record<string, string> = {
  scheduled: 'Sched',
  in_progress: 'InProg',
  completed: 'Comp',
  cancelled: 'Cncld',
  called_off: 'CldOff',
  no_show: 'NoShow',
  reschedule: 'Pend',
  rescheduled: 'Resched',
  partially_completed: 'PartComp',
  disqualified: 'DQ',
  blocked: 'DNH',
  overbooked: 'OvrBk',
  to_be_scheduled: 'TBS',
  invited: 'Inv',
};

export interface SessionDistribution {
  total: number;
  counts: Partial<Record<string, number>>;
}

/**
 * Latest bucket for one participant + one eligible session type: reuses the
 * same "active invite" / "latest schedule" / empty-status rules the nested
 * session grid already applies (see NestedGridStatusCell / SessionExpandCard),
 * so the Participant Status distribution and the Session view agree with what
 * the (now-removed) expanded grid used to show.
 */
function resolveSessionTypeBucket(
  participant: Participant,
  sessionType: SessionTypeLike,
  participantSchedules: ScheduleLike[],
): {schedule: ScheduleLike | undefined; bucket: string} {
  const schedule = getLatestScheduleForSessionType(
    participantSchedules,
    sessionType.id_guid ?? sessionType.id,
    sessionType.name,
  ) as ScheduleLike | undefined;
  const canInviteThisSlot = !schedule || shouldShowBook(schedule);
  if (canInviteThisSlot && hasActiveInviteForSessionType(participant, sessionType)) {
    return {schedule, bucket: 'invited'};
  }
  if (!schedule || isSessionStatusEmpty(schedule.status)) {
    return {schedule, bucket: 'to_be_scheduled'};
  }
  const normalized = normalizeSessionStatus(schedule.status);
  return {schedule, bucket: normalized ?? 'to_be_scheduled'};
}

/**
 * OD-1097 plural sibling to resolveSessionTypeBucket: every one of the
 * participant's schedules for this session type gets its own row (oldest
 * first, so history reads chronologically per AC8), but only while the
 * session type's *current* allow_multiple_bookings_per_participant is on.
 * When it's off — including a cancel-then-rebook cycle on an
 * always-single-booking session type (OD-1174) — this collapses to just the
 * latest schedule, same as the singular resolveSessionTypeBucket, so a
 * superseded cancelled row doesn't linger in the grid as a phantom second
 * session. The forward-looking "book another" placeholder row stays gated on
 * the live toggle/cap, since that's the one that actually creates new
 * bookings.
 *
 * With the flag off, or with 0 or 1 real schedule, this returns exactly the
 * singular resolveSessionTypeBucket result wrapped in a one-element array —
 * byte-for-byte the old behaviour for the overwhelmingly common case.
 *
 * The placeholder row (schedule: undefined) lets "Book"/"Invite" stay
 * reachable — SessionView's isBookable already treats a rowless row as
 * bookable, so this needs no changes there. Bucket is 'invited' when an
 * active self-booking invite exists, else 'to_be_scheduled', mirroring the
 * singular fallback.
 */
function resolveSessionTypeBuckets(
  participant: Participant,
  sessionType: SessionTypeLike,
  participantSchedules: ScheduleLike[],
): Array<{schedule: ScheduleLike | undefined; bucket: string}> {
  const matchingSchedules = filterSchedulesForSessionType(
    participantSchedules,
    sessionType.id_guid ?? sessionType.id,
    sessionType.name,
  )
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.start_time || a.created_at || 0).getTime();
      const bTime = new Date(b.start_time || b.created_at || 0).getTime();
      return aTime - bTime;
    });

  const supportsMultipleBookings =
    sessionType.allow_multiple_bookings_per_participant ||
    sessionType.cap_participant_sessions_in_window;
  if (!supportsMultipleBookings) {
    return [resolveSessionTypeBucket(participant, sessionType, participantSchedules)];
  }

  // Every booking keeps its own row: cancelling, calling off or marking a
  // no-show updates the status of the row it happened to — it never removes the
  // row, or the action a user just took would vanish from the grid instead of
  // being reported back to them. Those statuses do free the participant's slot,
  // so only the forward-looking "book another" placeholder below keys off the
  // applicable legacy or rolling-window capacity rather than the row list.
  const rows: Array<{schedule: ScheduleLike | undefined; bucket: string}> =
    matchingSchedules.map((schedule) => ({
      schedule,
      bucket: isSessionStatusEmpty(schedule.status)
        ? 'to_be_scheduled'
        : (normalizeSessionStatus(schedule.status) ?? 'to_be_scheduled'),
    }));

  if (supportsMultipleBookings) {
    const countedSchedules = matchingSchedules.filter((s) =>
      isScheduleCountedTowardSessionTypeCap(s.status),
    );
    const cappedCount = countedSchedules.length;
    const max = sessionType.max_sessions_per_participant || 0;
    // A blank legacy max always has "room" — appending a placeholder row for it
    // would be a phantom row that never resolves once any real schedule exists.
    // Window-capped types instead use their current anchored window. This is a
    // display affordance only; booking-time validation remains authoritative.
    const hasRoom = sessionType.cap_participant_sessions_in_window
      ? hasRemainingWindowCapCapacity({
          enabled: true,
          basis: sessionType.window_limit_basis ?? null,
          windowDays: sessionType.window_days ?? null,
          capacity: sessionType.window_capacity ?? null,
          maxHoursPerDay: sessionType.max_hours_per_day ?? null,
          existingSessions: countedSchedules,
        })
      : (max ? cappedCount < max : cappedCount === 0);

    const hasActiveInvite = hasActiveInviteForSessionType(participant, sessionType);
    if (hasRoom || hasActiveInvite) {
      rows.push({
        schedule: undefined,
        bucket: hasActiveInvite ? 'invited' : 'to_be_scheduled',
      });
    }
  }

  // An eligible session type with no booking at all still needs its row, so the
  // participant can be booked or invited for it.
  if (rows.length === 0) {
    return [resolveSessionTypeBucket(participant, sessionType, participantSchedules)];
  }

  return rows;
}

/**
 * Per-participant distribution across eligible session types — single source
 * of truth for the "N sessions" count + mini-chip breakdown shown both in the
 * Participant Status smart-render cell and the Session view's group headers.
 */
export function computeParticipantSessionDistribution(
  eligibleSessionTypes: SessionTypeLike[],
  participantSchedules: ScheduleLike[],
  participant: Participant,
): SessionDistribution {
  const counts: Partial<Record<string, number>> = {};
  let total = 0;
  eligibleSessionTypes.forEach((st) => {
    resolveSessionTypeBuckets(participant, st, participantSchedules).forEach(({bucket}) => {
      counts[bucket] = (counts[bucket] || 0) + 1;
      total += 1;
    });
  });
  return {total, counts};
}

/** One session-view row: an eligible session type for a participant, with its latest schedule (if any). */
export interface SessionViewRow {
  /** schedule.id when booked, else a stable synthetic id for the unscheduled placeholder row. */
  rowId: string;
  participant: Participant;
  sessionType: SessionTypeLike;
  schedule?: ScheduleLike;
  bucket: string;
}

export interface SessionViewGroup {
  participant: Participant;
  rows: SessionViewRow[];
  distribution: SessionDistribution;
}

/**
 * Flattens every participant's eligible session types into session-view rows,
 * grouped by participant. Zero new API calls — reuses whatever schedule pool
 * the caller resolves per participant (project schedules already loaded once
 * by Scheduler.tsx).
 */
export function buildSessionViewGroups(
  participants: Participant[],
  eligibleSessionTypesByParticipantId: Record<string, SessionTypeLike[]>,
  resolveSchedulesForParticipant: (p: Participant) => ScheduleLike[],
): SessionViewGroup[] {
  return participants.map((participant) => {
    const eligible = eligibleSessionTypesByParticipantId[participant.id] || [];
    const schedules = resolveSchedulesForParticipant(participant);
    const counts: Partial<Record<string, number>> = {};
    let total = 0;
    const rows: SessionViewRow[] = eligible.flatMap((sessionType) => {
      const typeKey = sessionType.id_guid || sessionType.id || sessionType.name || '';
      return resolveSessionTypeBuckets(participant, sessionType, schedules).map(
        ({schedule, bucket}) => {
          counts[bucket] = (counts[bucket] || 0) + 1;
          total += 1;
          return {
            rowId: schedule?.id ? String(schedule.id) : `${participant.id}:${typeKey}`,
            participant,
            sessionType,
            schedule,
            bucket,
          };
        },
      );
    });
    return {
      participant,
      rows,
      distribution: {total, counts},
    };
  });
}

/** Active row-scoped filters for the Session view — same shape the page-level filter dialog produces. */
export interface SessionViewRowFilters {
  selectedSessionStatuses?: Set<string>;
  selectedSessionNames?: Set<string>;
  /** Lowercased 'onsite'/'offsite' delivery-mode values (Session Type filter). */
  selectedSessionTypeModes?: Set<string>;
  filterUnassignedPrimaryModeratorOnly?: boolean;
  /** Lowercased selected real moderator names (independent of the unassigned-only toggle). */
  selectedModeratorNames?: Set<string>;
}

/**
 * True when a single session-view row satisfies every active row-scoped
 * filter. Mirrors the pre-redesign `computeDisplayedSessionTypes` (formerly
 * in ParticipantRow.tsx) but keys status matching off `row.bucket` —
 * `resolveSessionTypeBucket` already resolves 'invited' vs 'to_be_scheduled'
 * vs a real normalized status as ONE mutually-exclusive value per row, so
 * there's no separate "is this slot invited AND ALSO to-be-scheduled" case
 * to guard against here (that was the source of a past bug when the two
 * were tracked as independent booleans).
 */
const matchesStatusFilter = (row: SessionViewRow, selected?: Set<string>): boolean => {
  if (!selected?.size) return true;
  const selectedLower = new Set([...selected].map((s) => s.toLowerCase()));
  return selectedLower.has(row.bucket);
};

const matchesSessionNameFilter = (row: SessionViewRow, selected?: Set<string>): boolean => {
  if (!selected?.size) return true;
  const name = scheduleExpandedGridSessionName(row.schedule, row.sessionType);
  return Boolean(name) && selected.has(name as string);
};

const matchesSessionTypeModeFilter = (row: SessionViewRow, selected?: Set<string>): boolean => {
  if (!selected?.size) return true;
  const label = formatSessionLocationTypeLabel(row.sessionType.session_type).toLowerCase();
  return selected.has(label);
};

const matchesModeratorFilter = (
  row: SessionViewRow,
  filterUnassignedPrimaryModeratorOnly: boolean | undefined,
  selectedModeratorNames?: Set<string>,
): boolean => {
  if (!filterUnassignedPrimaryModeratorOnly && !selectedModeratorNames?.size) return true;
  if (!row.schedule) return false;
  const {primary, secondary} = scheduleModeratorNames(row.schedule);
  if (filterUnassignedPrimaryModeratorOnly && primary.length === 0) return true;
  if (selectedModeratorNames?.size) {
    const all = [...primary, ...secondary].map((n) => n.toLowerCase());
    if (all.some((n) => selectedModeratorNames.has(n))) return true;
  }
  return false;
};

export function sessionViewRowMatchesFilters(
  row: SessionViewRow,
  filters: SessionViewRowFilters,
): boolean {
  const {
    selectedSessionStatuses,
    selectedSessionNames,
    selectedSessionTypeModes,
    filterUnassignedPrimaryModeratorOnly,
    selectedModeratorNames,
  } = filters;
  if (!matchesStatusFilter(row, selectedSessionStatuses)) return false;
  if (!matchesSessionNameFilter(row, selectedSessionNames)) return false;
  if (!matchesSessionTypeModeFilter(row, selectedSessionTypeModes)) return false;
  if (!matchesModeratorFilter(row, filterUnassignedPrimaryModeratorOnly, selectedModeratorNames)) return false;
  return true;
}

/** Narrows every group's rows to those matching the active Session view filters; distribution (the group-header chip summary) is left as-is — it always reflects the participant's full session set, not the current row-scoped filter selection. */
export function filterSessionViewGroups(
  groups: SessionViewGroup[],
  filters: SessionViewRowFilters,
): SessionViewGroup[] {
  const hasActiveFilter =
    Boolean(filters.selectedSessionStatuses?.size) ||
    Boolean(filters.selectedSessionNames?.size) ||
    Boolean(filters.selectedSessionTypeModes?.size) ||
    Boolean(filters.filterUnassignedPrimaryModeratorOnly) ||
    Boolean(filters.selectedModeratorNames?.size);
  if (!hasActiveFilter) return groups;
  return groups.map((g) => ({
    ...g,
    rows: g.rows.filter((row) => sessionViewRowMatchesFilters(row, filters)),
  }));
}
